/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import { create } from 'zustand';

/**
 * Shared state for the in-app help drawer.
 *
 * The drawer is mounted ONCE (in Layout, or on a registration wizard), while the button that opens it
 * appears in several places — the desktop header, the mobile header, the mobile menu. Desktop and
 * mobile headers are separate DOM subtrees and only one of them is visible at a time, so a single
 * component cannot render both buttons; a tiny shared store lets every button open the one drawer.
 *
 * `hasCard` is published by the drawer after it probes the API, so a button can hide itself on a page
 * that has no help text instead of opening an empty panel.
 */
interface HelpState {
  isOpen: boolean;
  /** False until the drawer confirms the current page has a help card. */
  hasCard: boolean;
  open: () => void;
  close: () => void;
  setHasCard: (hasCard: boolean) => void;
}

export const useHelpStore = create<HelpState>((set) => ({
  isOpen: false,
  hasCard: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  setHasCard: (hasCard) => set({ hasCard }),
}));

export default useHelpStore;
